/* eslint-disable @typescript-eslint/no-explicit-any */
import { format, parse, parseISO } from 'date-fns';
import { Guid } from 'guid-typescript';
import { firstValueFrom, forkJoin, lastValueFrom } from 'rxjs';
import {
  CountriesService,
  CountryVm,
  MrhSubscriptionInformationModel,
  PhonePrefixesService,
  PhonePrefixesVm,
  PolicyOverviewVm,
} from 'src/app/api';
import { EndorsementService } from 'src/app/api/api/endorsement.service';
import { Avenant } from 'src/app/api/model/avenant';
import { DeclarationState } from 'src/app/core/declaration.store';
import { optionItemState } from 'src/app/core/offer.store';
import { getProductFromCode } from 'src/app/shared/constants/product.constants';
import {
  Etage,
  NatureResidence,
  QualiteOccupant,
  SurfaceDependance,
  TypeHabitation,
} from 'src/app/shared/enum/housing.enum';
import { PathType } from 'src/app/shared/enum/path-type.enum';
import { SpaceOfRoom } from 'src/app/shared/enum/spaceOfRoom.enum';
import { Dependances } from 'src/app/shared/models/interface/concretes/dependances.interface';
import { KeyAmount } from 'src/app/shared/models/interface/concretes/key-amount.interface';
import { PackageVM } from 'src/app/shared/models/interface/concretes/offer-customize-custom.interface';
import { civiliteObject } from 'src/app/shared/models/model/concretes/shared/civility.model';
import { Rooms } from 'src/app/shared/models/model/concretes/shared/rooms.model';
import { DevisDocumentType } from '../constants/quotation-document-type.constants';
import { DeductibleType, getDefaultDeductibleTypeByPath } from '../enum/deductible-type.enum';
import { YearHouseBuilded } from '../enum/house-builded.enum';
import { QuotationSummaryResponse } from '../models/model/concretes/shared/quotation-summary.model';
import { mapToEnumValue } from './enum-mapper.util';
import { getDeclarationState } from './riskitem-util';
import { AvenantOverviewResponse } from 'src/app/api/model/avenantOverviewResponse';

//#region Devis
export function dispatchDeclaration(store: any, dataDevis: QuotationSummaryResponse) {
  store.updateDeclaration(getDeclarationState(dataDevis));
}

export function dispatchDevis(store: any, dataDevis: QuotationSummaryResponse) {
  store.updateDevis({
    NumeroDevis: dataDevis.reference,
    DevisId: dataDevis.quotationId,
    DevisStatus: dataDevis.quotationStateId,
    GDAPositif: !!dataDevis.clientInformations.gdaTypeId,
    PPEPositif: dataDevis.clientInformations.isPpe,
    PrimeMensuelle: dataDevis.summaryInformation.monthlyPremiumTTC,
    PrimeAnnuelle: dataDevis.summaryInformation.annualPremiumTTC,
    AgreementId: dataDevis.agreementId,
    DocumentDevis: {
      documentId: dataDevis.summaryInformation.quotationFileId.toString(),
      documentTypeCode: DevisDocumentType.DEV,
    },
    DocumentFIC: {
      documentId: dataDevis.summaryInformation.ficFileId.toString(),
      documentTypeCode: DevisDocumentType.FIC,
    },
    DocumentInfo: {
      documentId: dataDevis.summaryInformation.productInformationFileId.toString(),
      documentTypeCode: DevisDocumentType.DIPA,
    },
    DocumentCG: {
      documentId: dataDevis.summaryInformation.generalConditionFileId.toString(),
      documentTypeCode: DevisDocumentType.CG,
    },
    ClientAdvisorFullName: dataDevis.summaryInformation.clientAdvisorFullName,
  });
}

export function dispatchOffer(store: any, dataDevis: QuotationSummaryResponse) {
  store.updateReferenceDevis(dataDevis.reference);
  store.updateMensualPrice(dataDevis.summaryInformation.monthlyPriceSocleTTC.toFixed(2));
  store.updateSelectedOffer(dataDevis.formulasAndOptionsInformations.formulaCode);

  // Display promotion code and type in summary offer if exist
  if (dataDevis.summaryInformation.promotionalCode) {
    const summary: KeyAmount[] = [
      {
        key: `Code promotionnel${dataDevis.summaryInformation.promotionalCodeType ? ` (${dataDevis.summaryInformation.promotionalCodeType})` : ''}`,
        value: dataDevis.summaryInformation.promotionalCode,
      },
    ];

    store.updateOptions(summary);
  }

  store.updateCouponCode(dataDevis.summaryInformation.promotionalCode);
  store.updateTypePromotion(dataDevis.summaryInformation.promotionalCodeType);
}

const OPTION_DISPLAY_ORDER = ['objets de valeur', 'dependances', 'amenagements exterieurs', 'assurance scolaire'];

const RESIDENCE_TYPE_VALUES = [
  NatureResidence.Principale,
  NatureResidence.Secondaire,
  NatureResidence.ForRent,
  NatureResidence.Etudiant,
  NatureResidence.Chambre,
  NatureResidence.ChambreCROUS,
  NatureResidence.Inconnue,
];
const RESIDENCE_TYPE_ALIASES = {
  principal: NatureResidence.Principale,
  principale: NatureResidence.Principale,
  primary: NatureResidence.Principale,
  secondary: NatureResidence.Secondaire,
  secondaire: NatureResidence.Secondaire,
  etudiant: NatureResidence.Etudiant,
  student: NatureResidence.Etudiant,
  forrent: NatureResidence.ForRent,
  pourlocation: NatureResidence.ForRent,
};

const OCCUPANT_QUALITY_VALUES = [QualiteOccupant.Proprietaire, QualiteOccupant.Locataire, QualiteOccupant.Colocataire];
const OCCUPANT_QUALITY_ALIASES = {
  owner: QualiteOccupant.Proprietaire,
  proprietaire: QualiteOccupant.Proprietaire,
  tenant: QualiteOccupant.Locataire,
  locataire: QualiteOccupant.Locataire,
  roommate: QualiteOccupant.Colocataire,
  colocataire: QualiteOccupant.Colocataire,
};

const HOUSING_TYPE_VALUES = [TypeHabitation.Appartement, TypeHabitation.Maison, TypeHabitation.Autres];
const HOUSING_TYPE_ALIASES = {
  house: TypeHabitation.Maison,
  maison: TypeHabitation.Maison,
  apartment: TypeHabitation.Appartement,
  appartement: TypeHabitation.Appartement,
  flat: TypeHabitation.Appartement,
  other: TypeHabitation.Autres,
  autres: TypeHabitation.Autres,
};

const APARTMENT_FLOOR_VALUES = [Etage.RezDeChaussee, Etage.Intermediaire, Etage.DernierEtage];
const APARTMENT_FLOOR_ALIASES = {
  groundfloor: Etage.RezDeChaussee,
  rezdechaussee: Etage.RezDeChaussee,
  middlefloor: Etage.Intermediaire,
  intermediate: Etage.Intermediaire,
  intermediaire: Etage.Intermediaire,
  topfloor: Etage.DernierEtage,
  dernieretage: Etage.DernierEtage,
};

const CONSTRUCTION_YEAR_VALUES = [
  YearHouseBuilded.BEFORE_1915,
  YearHouseBuilded.FROM_1915_TO_1948,
  YearHouseBuilded.FROM_1949_TO_1967,
  YearHouseBuilded.FROM_1968_TO_1974,
  YearHouseBuilded.FROM_1975_TO_1981,
  YearHouseBuilded.FROM_1982_TO_1989,
  YearHouseBuilded.FROM_1990_TO_1999,
  YearHouseBuilded.FROM_2000_TO_2005,
  YearHouseBuilded.FROM_2006_TO_2012,
  YearHouseBuilded.AFTER_2013,
  YearHouseBuilded.Inconnue,
];
const CONSTRUCTION_YEAR_ALIASES = {
  before1915: YearHouseBuilded.BEFORE_1915,
  between1915and1948: YearHouseBuilded.FROM_1915_TO_1948,
  entre1915et1948: YearHouseBuilded.FROM_1915_TO_1948,
  between1949and1967: YearHouseBuilded.FROM_1949_TO_1967,
  entre1949et1967: YearHouseBuilded.FROM_1949_TO_1967,
  between1968and1974: YearHouseBuilded.FROM_1968_TO_1974,
  entre1968et1974: YearHouseBuilded.FROM_1968_TO_1974,
  between1975and1981: YearHouseBuilded.FROM_1975_TO_1981,
  entre1975et1981: YearHouseBuilded.FROM_1975_TO_1981,
  between1982and1989: YearHouseBuilded.FROM_1982_TO_1989,
  entre1982et1989: YearHouseBuilded.FROM_1982_TO_1989,
  between1990and1999: YearHouseBuilded.FROM_1990_TO_1999,
  entre1990et1999: YearHouseBuilded.FROM_1990_TO_1999,
  between2000and2005: YearHouseBuilded.FROM_2000_TO_2005,
  entre2000et2005: YearHouseBuilded.FROM_2000_TO_2005,
  between2006and2012: YearHouseBuilded.FROM_2006_TO_2012,
  entre2006et2012: YearHouseBuilded.FROM_2006_TO_2012,
  after2013: YearHouseBuilded.AFTER_2013,
  unknown: YearHouseBuilded.Inconnue,
};

const DEDUCTIBLE_VALUES = [DeductibleType.CentCinquante, DeductibleType.TroisCents, DeductibleType.CinqCents];
const DEDUCTIBLE_ALIASES = {
  centcinquante: DeductibleType.CentCinquante,
  troiscents: DeductibleType.TroisCents,
  cinqcents: DeductibleType.CinqCents,
};

function normalizeLabel(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase();
}

function getRiskCeilingValue(
  dataDevis: QuotationSummaryResponse,
  code: string
): string | null {
  const rawValue = dataDevis.riskItemInformations.values
    .find((risk) => risk.code === code)?.value;

  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);

    const value = Array.isArray(parsed) && parsed.length > 0
      ? parsed[0]
      : parsed;

    const numberValue = Number(value);

    if (Number.isNaN(numberValue)) {
      return String(value);
    }

    return numberValue.toLocaleString('fr-FR');
  } catch {
    return null;
  }
}

function getLabelIndexForOrdering(label: string): number {
  const normalized = normalizeLabel(label);
  const index = OPTION_DISPLAY_ORDER.findIndex((entry) => normalized.includes(entry));

  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function formatObjectsDeValeurLabel(dataDevis: QuotationSummaryResponse): string {
  const ceiling = getRiskCeilingValue(dataDevis, 'OBJVAL');
  return ceiling ? `Objets de valeur : ${ceiling} € de plafond` : 'Objets de valeur';
}

function formatDependancesLabel(dataDevis: QuotationSummaryResponse): string {
  const ceiling = getRiskCeilingValue(dataDevis, 'RPM');

  return ceiling ? `Plafond Mobilier : ${ceiling} € de plafond` : 'Plafond Mobilier';
}

function mapOptionLabel(label: string, dataDevis: QuotationSummaryResponse): string {
  const normalizedLabel = normalizeLabel(label);

  if (normalizedLabel.includes('renforcement des objets de valeur')) {
    return formatObjectsDeValeurLabel(dataDevis);
  }

  if (normalizedLabel.includes('renforcement du patrimoine mobilier')) {
    return formatDependancesLabel(dataDevis);
  }

  if (normalizedLabel.includes('amenagements exterieurs')) {
    return 'Aménagements extérieurs';
  }

  if (normalizedLabel.includes('assurance scolaire')) {
    return 'Assurance scolaire';
  }

  if (normalizedLabel.includes('bris de vitres sur les biens immobiliers')) {
    return 'Bris de vitres';
  }

  return label;
}

function ensureOptionExists(optionSelected: KeyAmount[], optionLabel: string): void {
  const normalizedTarget = normalizeLabel(optionLabel);
  const exists = optionSelected.some((option) => normalizeLabel(option.key ?? '').includes(normalizedTarget));

  if (!exists) {
    optionSelected.push({
      key: optionLabel,
      value: '',
    });
  }
}

export function dispatchOptions(store: any, dataDevis: QuotationSummaryResponse) {
  console.log('Dispatching options with dataDevis:', dataDevis);
  // update package in summary offer
  const pkge: PackageVM = {
    code: dataDevis.formulasAndOptionsInformations.formulaCode,
    text: dataDevis.formulasAndOptionsInformations.formulaCode,
    comparatorChoice: false,
    monthlyPremiumAmountIncludingTax: dataDevis.summaryInformation.monthlyPriceSocleTTC,
    urlWarranty: '',
    coverageDurationInMonths: 12,
    extraInformations: [],
    optionalCoverages: {},
    renfCoverages: {},
  };

  store.updateSummaryOffer(pkge);

  const optionSelected: KeyAmount[] = dataDevis.formulasAndOptionsInformations.options
    .filter((o) => o.isSelected)
    .map((o) => ({
      key: mapOptionLabel(o.label, dataDevis),
      value: String(o.pricedMonthlyAmount),
    }));

  ensureOptionExists(optionSelected, formatObjectsDeValeurLabel(dataDevis));
  ensureOptionExists(optionSelected, formatDependancesLabel(dataDevis));

  optionSelected.sort((a, b) => {
    return getLabelIndexForOrdering(a.key ?? '') - getLabelIndexForOrdering(b.key ?? '');
  });

  // display promotion code and type in summary offer if exist
  if (dataDevis.summaryInformation.promotionalCode) {
    optionSelected.unshift({
      key: `Code promotionnel ${dataDevis.summaryInformation.promotionalCodeType ? `(${dataDevis.summaryInformation.promotionalCodeType})` : ''}`,
      value: dataDevis.summaryInformation.promotionalCode,
    });
  }

  store.updateOptions(optionSelected);
}

export function dispatchPath(store: any, dataDevis: QuotationSummaryResponse) {
  const product = getProductFromCode(dataDevis.formulasAndOptionsInformations.formulaCode);
  const path: PathType = product ? (product.code as String as PathType) : PathType.UNDEFINED;
  store.updatePath(path);
}

export async function dispatchSubscriber(
  store: any,
  countriesService: CountriesService,
  phonePrefixesService: PhonePrefixesService,
  dataDevis: QuotationSummaryResponse,
) {
  const parsedDate = parse(dataDevis.clientInformations.birthDate, 'yyyy-MM-dd', new Date());
  const birth_country = await getCountryByName(countriesService, dataDevis.clientInformations.birthCountry);

  const nationality = await getCountryByName(countriesService, dataDevis.clientInformations.nationality);

  let phone_prefix = await getPhonePrefixesByName(phonePrefixesService, dataDevis.clientInformations.phonePrefix);
  if (!phone_prefix) {
    phone_prefix = store.subscriber().phone_prefix.indicator;
  }

  store.updateSubscriber({
    civility: civiliteObject.find((m) => m.text === dataDevis.clientInformations.civility) ?? null,
    lastname: dataDevis.clientInformations.lastName,
    firstname: dataDevis.clientInformations.firstName,
    email: dataDevis.clientInformations.email,
    birth_date: format(parsedDate, 'dd/MM/yyyy HH:mm:ss'),
    birth_postal_code: dataDevis.clientInformations.zipCode,
    birth_city: dataDevis.clientInformations.birthCity,
    // Preserve existing values if not well retrived from name or code
    birth_country: birth_country ?? store.subscriber().birth_country,
    nationality: nationality ?? store.subscriber().nationality,
    phone_prefix: {
      indicator: phone_prefix,
      phone: dataDevis.clientInformations.cellPhone ?? '',
      phone_consent: false,
      marketing_consent: false,
    },
    is_client: dataDevis.clientInformations.isEnrolled,
  });
}
//#endregion

//#region Contrat
export function dispatchPolicy(store: any, dataPolicy: PolicyOverviewVm) {
  store.updateContract({
    RefContrat: dataPolicy.reference,
    NumeroContrat: dataPolicy.agreementId?.toString(),
  });
}

export function dispatchSubscriber_C(store: any, dataPolicy: PolicyOverviewVm) {
  store.updateSubscriber({
    email: dataPolicy.memberEmail,
  });
}

export function dispatchPayment(store: any, dataPolicy: PolicyOverviewVm) {
  store.updatePhonePrefixSubscriber({
    indicator: { prefix: dataPolicy.memberPhonePrefix },
    phone: dataPolicy.memberPhoneNumber,
    phone_consent: false,
    marketing_consent: false,
  });
  store.updatePhonePrefixDirectDebit({
    indicator: { prefix: dataPolicy.memberPaymentPhonePrefix },
    phone: dataPolicy.memberPaymentPhoneNumber,
    phone_consent: false,
    marketing_consent: false,
  });
}

export function dispatchDevis_C(store: any, dataPolicy: PolicyOverviewVm) {
  store.updateDevis({
    DevisId: dataPolicy.quotationId,
    AgreementId: dataPolicy.agreementId,
  });
}

export function dispatchAvenant(store: any, dataAvenant: Avenant[]) {
  console.log('Dispatching endorsement data:', dataAvenant);
  const endorsementUpdates = dataAvenant.map((dataAvenant) => ({
    NumeroContrat: dataAvenant.agreementId,
    RefContrat: dataAvenant.reference,
    ClientAdvisorFullName: '',
    productName: dataAvenant.productName,
    correlationId: dataAvenant.correlationId,
    isChecked: false,
  }));
  store.updateAvenant({
    Avenants: endorsementUpdates,
  });
}

/**
 * Mappe un MrhSubscriptionInformationModel (issu du contexte back-office)
 * vers le store de déclaration.
 * Doit être appelé en dernier dans la chaîne de dispatch pour que les valeurs
 * du contexte aient la priorité sur celles issues de la reprise de devis.
 */
export function dispatchDeclarationFromContext(store: any, declaration: MrhSubscriptionInformationModel): void {
  // Le backend peut retourner des propriétés en PascalCase (C# convention) ou camelCase.
  // `d` permet de lire les deux formes de manière transparente.
  const d = declaration as any;
  const get = (camel: string, pascal: string): any =>
    d[camel] !== undefined && d[camel] !== null ? d[camel] : d[pascal];

  const numberOfRooms: Rooms = {
    numberOfRooms: get('roomCount', 'RoomCount') ?? 0,
    between41And75: get('roomCountGt40M2', 'RoomCountGt40M2') ?? null,
    moreThan76: get('roomCountGt76M2', 'RoomCountGt76M2') ?? null,
    hasVeranda: mapToEnumValue(
      get('verandaType', 'VerandaType'),
      [SpaceOfRoom.Aucune, SpaceOfRoom.lessThan10, SpaceOfRoom.between10And40, SpaceOfRoom.moreThan40],
      SpaceOfRoom.Aucune,
      {
        none: SpaceOfRoom.Aucune,
      },
    ),
  };

  const rawDependencyType = mapToEnumValue(
    get('dependencyType', 'DependencyType'),
    [
      SurfaceDependance.Aucune,
      SurfaceDependance.MoinsDe40m2,
      SurfaceDependance.De40a100m2,
      SurfaceDependance.PlusDe100m2,
      SurfaceDependance.PlusDe250m2,
      SurfaceDependance.PlusDe40m2,
      SurfaceDependance.MoinsDe9m2,
      SurfaceDependance.Entre9m2Et40m2,
      SurfaceDependance.Inconnue,
    ],
    SurfaceDependance.Aucune,
    {
      none: SurfaceDependance.Aucune,
      unknown: SurfaceDependance.Inconnue,
    },
  );
  const dependances: Dependances = {
    hasGarage: get('hasGarage', 'HasGarage') ?? null,
    hasBasement: get('hasBasement', 'HasBasement') ?? null,
    hasOtherDependance: rawDependencyType !== SurfaceDependance.Aucune ? !!rawDependencyType : null,
    otherDependanceSurface: rawDependencyType !== SurfaceDependance.Aucune ? rawDependencyType : null,
  };

  const rawQuotationDate = get('quotationDate', 'QuotationDate');
  const effectDate = rawQuotationDate ? parseISO(rawQuotationDate) : null;

  // `Floor` est le nom backend pour `apartmentFloor`
  const resolvedHousingType = mapToEnumValue(
    get('housingType', 'HousingType'),
    HOUSING_TYPE_VALUES,
    TypeHabitation.Appartement,
    HOUSING_TYPE_ALIASES,
  );
  const resolvedApartmentFloor = mapToEnumValue(
    get('apartmentFloor', 'ApartmentFloor') ?? get('floor', 'Floor'),
    APARTMENT_FLOOR_VALUES,
    Etage.Intermediaire,
    APARTMENT_FLOOR_ALIASES,
  );

  // Construit l'update de manière conditionnelle : seuls les champs fournis par
  // le contexte (non null/undefined) sont mis à jour. Cela évite d'écraser les
  // valeurs saisies par l'utilisateur lors d'un refresh ou d'une re-activation
  // de la route parent (contextDeclarationResolver).
  const partialUpdate: Partial<DeclarationState> = {};

  const residenceType = mapToEnumValue(
    get('residenceType', 'ResidenceType'),
    RESIDENCE_TYPE_VALUES,
    NatureResidence.Inconnue,
    RESIDENCE_TYPE_ALIASES,
  );
  if (residenceType != null) partialUpdate.residenceType = residenceType;

  const qualiteOccupant = mapToEnumValue(
    get('occupationStatus', 'OccupationStatus'),
    OCCUPANT_QUALITY_VALUES,
    QualiteOccupant.Locataire,
    OCCUPANT_QUALITY_ALIASES,
  );
  if (qualiteOccupant != null) partialUpdate.qualiteOccupant = qualiteOccupant;

  const hasClaims = get('hasClaims', 'HasClaims');
  if (hasClaims != null) partialUpdate.susbcriberHadClaimsInLast3Years = hasClaims;

  // Note: schoolInsuranceType, hasSchoolInsuranceOption, hasOptionVelo et
  // hasNomadBusinessOption n'ont pas de mapping direct dans le store.

  const hasSwimmingPool = get('hasSwimmingPool', 'HasSwimmingPool');
  if (hasSwimmingPool != null) partialUpdate.hasSwimmingPoolSpaOrJacuzzi = hasSwimmingPool;

  const hasGreenEnergy = get('hasGreenEnergyProductionInstallation', 'HasGreenEnergyProductionInstallation');
  if (hasGreenEnergy != null) partialUpdate.durableDevelopmentInstallation = hasGreenEnergy;

  const constructionYear = mapToEnumValue(
    get('constructionYear', 'ConstructionYear'),
    CONSTRUCTION_YEAR_VALUES,
    YearHouseBuilded.Inconnue,
    CONSTRUCTION_YEAR_ALIASES,
  );
  if (constructionYear != null) partialUpdate.dateHouseBuilted = constructionYear;

  const roommatesNumber = get('roommatesNumber', 'RoommatesNumber');
  if (roommatesNumber != null) partialUpdate.nbColoc = roommatesNumber;

  const deductibleDefault = getDefaultDeductibleTypeByPath(store.path());
  const rawDeductibleType = mapToEnumValue(
    get('deductibleType', 'DeductibleType'),
    DEDUCTIBLE_VALUES,
    deductibleDefault,
    DEDUCTIBLE_ALIASES,
  );
  if (rawDeductibleType) partialUpdate.deductibleType = rawDeductibleType;

  const objetValue = get('valueObjectOptionType', 'ValueObjectOptionType');
  if (objetValue != null) partialUpdate.objetValue = objetValue;

  if (rawQuotationDate) partialUpdate.effect_date = effectDate;
  const roomCountValue = get('roomCount', 'RoomCount');
  if (roomCountValue != null && roomCountValue > 0) partialUpdate.numberOfRooms = numberOfRooms;
  if (rawDependencyType != null) partialUpdate.dependances = dependances;
  if (resolvedHousingType) partialUpdate.housingType = resolvedHousingType;
  if (resolvedApartmentFloor) partialUpdate.apartmentFloor = resolvedApartmentFloor;

  store.updateDeclaration(partialUpdate);

  const rawQuotationReference = get('quotationReference', 'QuotationReference');
  if (rawQuotationReference) {
    store.updateReferenceDevis(rawQuotationReference);
  }
}
//#endregion

async function getCountryByName(
  countriesService: CountriesService,
  nameOrCode: string | null | undefined,
): Promise<CountryVm | null> {
  if (!nameOrCode) return null;
  const countries = await firstValueFrom(countriesService.apiCountriesGet());
  return countries.find((c) => c.countryNameFr === nameOrCode || c.countryCode === nameOrCode) ?? null;
}

async function getPhonePrefixesByName(
  phonePrefixesService: PhonePrefixesService,
  prefix: string,
): Promise<PhonePrefixesVm | null> {
  const phonePrefixes = await firstValueFrom(phonePrefixesService.apiPhonePrefixesGet());
  return phonePrefixes.find((p) => p.prefix === prefix) ?? null;
}

export async function processEndorsementOverview(
  endorsementService: EndorsementService,
  contextId: string,
  email: string,
  estSigne: boolean,
  avenants: Avenant[],
) {
  const observables = avenants.map((avenant) =>
    endorsementService.apiEndorsementOverviewGet(avenant.agreementId, Guid.parse(contextId)?.toJSON().value, email),
  );

  console.log('Created observables for endorsement overview:', observables);

  // // Attendre que toutes les requêtes soient terminées
  return await lastValueFrom(forkJoin(observables));
}


/**
 * Transforme un objet optionalCoverages en liste d'optionItemState.
 * Chaque clé de l'objet devient le `code`, et la première valeur numérique
 * de l'objet imbriqué devient le `amount`.
 */
export function mapOptionalCoveragesToOptionItems(
  optionalCoverages: { [key: string]: { [key: string]: number } } | null | undefined,
): optionItemState[] {
  if (!optionalCoverages) return [];
  return Object.entries(optionalCoverages).map(([code, innerObj]) => ({
    isSelected: false,
    amount: Object.values(innerObj)[0] ?? 0,
    code,
  }));
}


export function dispatchEndorsementOverview(
  store: any,
  overview: AvenantOverviewResponse | undefined,
  avenants: Avenant[],
) {
  
  store.updateContract({
    NumeroContrat: (overview?.agreementId ?? avenants[0]?.agreementId)?.toString(),
    RefContrat: overview?.reference ?? avenants[0]?.reference,
  });

  store.updateSubscriber({
    email: overview?.memberEmail ?? avenants[0]?.email,
  });

  store.updatePhonePrefixSubscriber({
    indicator: { prefix: overview?.memberPhonePrefix ?? '' },
    indicatorString: overview?.memberPhonePrefix ?? '',
    phone: overview?.memberPhoneNumber ?? '',
    phone_consent: false,
  });

  store.updatePhonePrefixDirectDebit({
    indicator: { prefix: overview?.memberPaymentPhonePrefix ?? '' },
    indicatorString: overview?.memberPaymentPhonePrefix ?? '',
    phone: overview?.memberPaymentPhoneNumber ?? '',
    phone_consent: false,
  });
}
